import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { get, post } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen, Modal } from "../../../components/admin/EssUi";
import { BarChart3, Search } from "lucide-react";

const fmtTime = (dt) => {
  if (!dt) return "—";
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const fmtHours = (h) => {
  if (h == null) return "—";
  return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
};

const isLate = (checkIn) => {
  if (!checkIn) return false;
  const d = new Date(checkIn);
  return d.getHours() >= 9 && d.getMinutes() > 0;
};

const todayStr = () => {
  const d = new Date();
  return d.toISOString().split("T")[0];
};

const AdminPanelAttendancePage = () => {
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManageAttendance;
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("today");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ employee_id: "", check_in: "", check_out: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const PAGE_SIZE = 20;

  const loadData = useCallback(async () => {
    try {
      const td = todayStr();

      const [empRows, leaves, attRows] = await Promise.all([
        get("/api/employees?limit=500"),
        get("/api/leave-requests"),
        get("/api/attendance"),
      ]);

        const allEmployees = empRows?.data || [];
        setEmployees(allEmployees);

        const empMap = {};
        for (const e of allEmployees) { empMap[e.id] = e; }

        const todayAttendances = (attRows?.data || []).filter((a) => {
          const d = a.check_in ? new Date(a.check_in).toISOString().split("T")[0] : null;
          return d === td;
        });
        const attMap = {};
        for (const a of todayAttendances) {
          const empId = a.employee_id;
          if (empId != null) attMap[empId] = a;
        }

        const validatedLeaves = (leaves?.data || []).filter(
          (l) => l.state === "validate" && l.date_from <= td && l.date_to >= td
        );
        const onLeaveIds = new Set(validatedLeaves.map((l) => l.employee_id).filter(Boolean));

        const allEmpIds = new Set(allEmployees.filter((e) => e.active !== false).map((e) => e.id));
        const absentIds = [...allEmpIds].filter((id) => !onLeaveIds.has(id));
        const absentRows = absentIds.map((id) => {
          const emp = empMap[id] || {};
          const att = attMap[id];
          let status = "Absent";
          let checkIn = "—";
          let checkOut = "—";
          let hours = null;
          if (att) {
            checkIn = att.check_in ? fmtTime(att.check_in) : "—";
            checkOut = att.check_out ? fmtTime(att.check_out) : "—";
            hours = att.worked_hours != null ? Number(att.worked_hours) : null;
            const ci = att.check_in ? new Date(att.check_in) : null;
            const late = ci && ci.getHours() >= 9 && ci.getMinutes() > 0;
            status = late ? "Late" : "Present";
          } else if (onLeaveIds.has(id)) {
            status = "On Leave";
          }
          const hoursStr = hours != null ? `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m` : "—";
          return {
            id: `emp-${id}`,
            employee: emp.name || "—",
            employeeId: id,
            department: emp.department_id || "—",
            departmentId: emp.department_id,
            workMode: "On-site",
            checkIn,
            checkOut,
            hours,
            hoursStr,
            status,
          };
        });

        setAttendance(absentRows);
      } catch {}
      setLoaded(true);
    },
    []
  );

  useEffect(() => { loadData(); }, [loadData]);

  const depts = useMemo(() => [...new Set(attendance.map((r) => r.department).filter(Boolean))], [attendance]);

  const handleAddEntry = async () => {
    if (!addForm.employee_id || !addForm.check_in || !addForm.check_out) {
      alert("Please fill employee and both check times.");
      return;
    }
    setSubmitting(true);
    try {
      const start = new Date(addForm.check_in);
      const end = new Date(addForm.check_out);
      const workedHours = Math.max(0, Math.round(((end - start) / (1000 * 60 * 60)) * 100) / 100);
      await post("/api/attendance", {
        employee_id: Number(addForm.employee_id),
        check_in: new Date(addForm.check_in).toISOString(),
        check_out: new Date(addForm.check_out).toISOString(),
        worked_hours: workedHours,
        reason: (addForm.reason || "Manual entry").trim(),
      });
      setAddOpen(false);
      setAddForm({ employee_id: "", check_in: "", check_out: "", reason: "" });
      setLoaded(false);
      await loadData();
    } catch (e) {
      alert("Create failed: " + (e.message || "Unknown"));
    }
    setSubmitting(false);
  };

  const handleExportCSV = () => {
    const headers = ["Employee", "Department", "Check In", "Check Out", "Hours", "Status"];
    const rows = displayData.map((r) => [r.employee, r.department, r.checkIn, r.checkOut, r.hoursStr, r.status]);
    const csv = [headers.join(","), ...rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${todayStr()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    if (!loaded) return [];
    return attendance.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (deptFilter !== "all" && r.department !== deptFilter) return false;
      if (search.trim() && !r.employee.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [attendance, statusFilter, deptFilter, search, loaded]);

  const displayData = useMemo(() => {
    if (activeTab === "overtime") return filtered.filter((r) => r.hours != null && r.hours > 8);
    if (activeTab === "anomalies") return filtered.filter((r) => r.status === "Late" || r.status === "Absent");
    return filtered;
  }, [filtered, activeTab]);

  const totalPages = Math.max(1, Math.ceil(displayData.length / PAGE_SIZE));
  const paginatedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return displayData.slice(start, start + PAGE_SIZE);
  }, [displayData, page]);

  useEffect(() => { setPage(1); }, [search, deptFilter, statusFilter, activeTab]);

  const totalEmp = employees.filter((e) => e.active !== false).length;
  const present = attendance.filter((r) => r.status === "Present").length;
  const late = attendance.filter((r) => r.status === "Late").length;
  const absent = attendance.filter((r) => r.status === "Absent").length;
  const onLeave = attendance.filter((r) => r.status === "On Leave").length;
  const totalOvertime = attendance.reduce((s, r) => s + (r.hours != null && r.hours > 8 ? r.hours - 8 : 0), 0);

  const tabs = [
    { key: "today", label: "Today", count: attendance.length },
    { key: "overtime", label: "Overtime", count: attendance.filter((r) => r.hours != null && r.hours > 8).length },
    { key: "anomalies", label: "Anomalies", count: late + absent },
  ];

  const getStatusColor = (status) => {
    const c = { Present: "#16a34a", Late: "#d97706", Absent: "#e11d48", "On Leave": "#64748b" };
    return c[status] || "#64748b";
  };
  const getStatusBg = (status) => {
    const c = { Present: "#dcfce7", Late: "#fef3c7", Absent: "#fecdd3", "On Leave": "#f1f5f9" };
    return c[status] || "#f1f5f9";
  };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Attendance"
        subtitle="Monitor check-in, timesheets and overtime"
        actions={
          <div style={{ display: "flex", gap: "12px" }}>
            {canManage && (
            <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" style={{ background: "#059669", color: "white", border: "none", cursor: "pointer" }}>＋ Add Entry</button>
            )}
            <button onClick={handleExportCSV} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{ background: "#f1f5f9", color: "#334155", border: "none", cursor: "pointer" }}><BarChart3 size={16} /> Export</button>
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Present Today" value={String(present)} icon="✅" color="green" trend={`${totalEmp > 0 ? Math.round((present / totalEmp) * 100) : 0}% of headcount`} />
        <StatCard label="Late Arrivals" value={String(late)} icon="⚠️" color="amber" trend={`${late} after 09:00`} />
        <StatCard label="Absent" value={String(absent)} icon="❌" color="rose" trend={`${onLeave} on leave`} />
        <StatCard label="Overtime (Today)" value={totalOvertime > 0 ? `${Math.round(totalOvertime)}h` : "0h"} icon="⏰" color="indigo" trend="Across all teams" />
      </div>

      <div style={{ display: "flex", gap: "4px", marginBottom: "20px", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px", overflowX: "auto" }}>
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "8px 20px", border: "none", background: activeTab === tab.key ? "#059669" : "transparent",
            color: activeTab === tab.key ? "white" : "#475569", borderRadius: "8px", fontSize: "13px",
            fontWeight: activeTab === tab.key ? "600" : "500", cursor: "pointer", transition: "all 0.2s",
            display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap",
          }}>
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? "rgba(255,255,255,0.2)" : "#f1f5f9", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "600" }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee..."
            style={{ width: "100%", padding: "10px 12px 10px 36px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", outline: "none", transition: "all 0.2s", background: "#f8fafc" }}
            onFocus={(e) => e.target.style.borderColor = "#4f46e5"}
            onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
          />
        </div>
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
          style={{ padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", background: "#f8fafc", outline: "none", cursor: "pointer", minWidth: "160px" }}>
          <option value="all">All Departments</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", background: "#f8fafc", outline: "none", cursor: "pointer", minWidth: "160px" }}>
          <option value="all">All Status</option>
          {["Present", "Late", "Absent", "On Leave"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" defaultValue={todayStr()}
          style={{ padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", background: "#f8fafc", outline: "none", cursor: "pointer", minWidth: "140px" }}
        />
      </div>

      <div style={{ display: "grid", gap: "24px" }} className="ess-rg-2-1">
        <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "650px" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {["Employee", "Department", "Check In", "Check Out", "Hours", "Status"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: "40px 16px", textAlign: "center", color: "#94a3b8", fontSize: "14px" }}>No attendance data for today</td></tr>
                ) : (paginatedData.map((row, index) => (
                  <tr key={row.id} style={{ borderBottom: index < paginatedData.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <td style={{ padding: "10px 12px", fontSize: "13px", fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "160px" }} title={row.employee}>{row.employee}</td>
                    <td style={{ padding: "10px 12px", fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "140px" }} title={row.department}>{row.department}</td>
                    <td style={{ padding: "10px 12px", fontSize: "13px", color: row.checkIn === "—" ? "#94a3b8" : "#475569", fontFamily: "monospace", whiteSpace: "nowrap" }}>{row.checkIn}</td>
                    <td style={{ padding: "10px 12px", fontSize: "13px", color: row.checkOut === "—" ? "#94a3b8" : "#475569", fontFamily: "monospace", whiteSpace: "nowrap" }}>{row.checkOut}</td>
                    <td style={{ padding: "10px 12px", fontSize: "13px", fontWeight: "600", color: row.hours != null ? "#1e293b" : "#94a3b8", textAlign: "center", whiteSpace: "nowrap" }}>{row.hoursStr}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: "4px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "600" }}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: page <= 1 ? "#f8fafc" : "white", color: page <= 1 ? "#cbd5e1" : "#059669", cursor: page <= 1 ? "default" : "pointer", fontSize: "12px", fontWeight: "500" }}>‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 11px", border: "none", borderRadius: "6px", background: p === page ? "#059669" : "transparent", color: p === page ? "white" : "#64748b", cursor: "pointer", fontSize: "13px", fontWeight: p === page ? "600" : "400", minWidth: "30px" }}>{p}</button>
              ))}
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: page >= totalPages ? "#f8fafc" : "white", color: page >= totalPages ? "#cbd5e1" : "#059669", cursor: page >= totalPages ? "default" : "pointer", fontSize: "12px", fontWeight: "500" }}>Next ›</button>
              <span style={{ fontSize: "12px", color: "#94a3b8", marginLeft: "8px" }}>{displayData.length} total</span>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>Today's Overview</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { label: "Present", value: present, pct: totalEmp > 0 ? Math.round((present / totalEmp) * 100) : 0, color: "#16a34a" },
                { label: "Late", value: late, pct: totalEmp > 0 ? Math.round((late / totalEmp) * 100) : 0, color: "#d97706" },
                { label: "On Leave", value: onLeave, pct: totalEmp > 0 ? Math.round((onLeave / totalEmp) * 100) : 0, color: "#64748b" },
                { label: "Absent", value: absent, pct: totalEmp > 0 ? Math.round((absent / totalEmp) * 100) : 0, color: "#e11d48" },
              ].map((item) => (
                <div key={item.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "3px" }}>
                    <span style={{ color: "#64748b" }}>{item.label}</span>
                    <span style={{ fontWeight: "600", color: "#1e293b" }}>{item.value} ({item.pct}%)</span>
                  </div>
                  <div style={{ height: 8, background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${item.pct}%`, background: item.color, borderRadius: "4px", transition: "width 0.5s ease" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "white", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>Overtime by Department</h3>
            {(() => {
              const byDept = {};
              attendance.forEach((r) => {
                if (r.hours != null && r.hours > 8) {
                  const d = r.department || "Unknown";
                  byDept[d] = (byDept[d] || 0) + (r.hours - 8);
                }
              });
              const sorted = Object.entries(byDept).sort((a, b) => b[1] - a[1]);
              const maxVal = sorted.length > 0 ? Math.max(...sorted.map(([, v]) => v)) : 1;
              const colors = ["#4f46e5", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#9333ea", "#ea580c"];
              return sorted.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8", fontSize: "13px" }}>No overtime today</div>
              ) : (
                sorted.map(([dept, hours], i) => (
                  <div key={dept} style={{ marginBottom: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "3px" }}>
                      <span style={{ color: "#64748b" }}>{dept}</span>
                      <span style={{ fontWeight: "600", color: "#1e293b" }}>{Math.round(hours)}h</span>
                    </div>
                    <div style={{ height: 8, background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(hours / maxVal) * 100}%`, background: colors[i % colors.length], borderRadius: "4px", transition: "width 1s ease" }} />
                    </div>
                  </div>
                ))
              );
            })()}
          </div>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => { setAddOpen(false); setAddForm({ employee_id: "", check_in: "", check_out: "", reason: "" }); }} title="＋ Add Attendance Entry" size="md">
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Employee *</label>
            <select className="ess-inp" style={{ width: "100%" }} value={addForm.employee_id} onChange={(e) => setAddForm({ ...addForm, employee_id: e.target.value })}>
              <option value="">— Select —</option>
              {employees.filter((e) => e.active !== false).map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gap: 12 }} className="ess-rg-2">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Check In *</label>
              <input type="datetime-local" className="ess-inp" style={{ width: "100%" }} value={addForm.check_in} onChange={(e) => setAddForm({ ...addForm, check_in: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Check Out *</label>
              <input type="datetime-local" className="ess-inp" style={{ width: "100%" }} value={addForm.check_out} onChange={(e) => setAddForm({ ...addForm, check_out: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Reason</label>
            <input className="ess-inp" style={{ width: "100%" }} value={addForm.reason} onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })} placeholder="e.g. Manual entry" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20, borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
          <button onClick={() => { setAddOpen(false); setAddForm({ employee_id: "", check_in: "", check_out: "", reason: "" }); }} className="btn" style={{ background: "#f1f5f9", color: "#64748b", border: "none", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleAddEntry} disabled={submitting} className="btn" style={{ background: submitting ? "#94a3b8" : "#059669", color: "white", border: "none", cursor: "pointer" }}>
            {submitting ? "Creating..." : "＋ Create"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelAttendancePage;
